import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../lib/api.js';
import { formatDate } from '../../../lib/formatters.js';
import { useDemoMode } from '../../../context/DemoModeContext.js';

interface Comment {
  id: string;
  authorType: string;
  authorId: string | null;
  body: string;
  isSystemMessage: boolean;
  createdAt: string;
  authorName?: string;
}

interface ClaimCommentsThreadProps {
  claimId: string;
  comments: Comment[];
  onCommentPosted: () => void;
}

export function ClaimCommentsThread({
  claimId,
  comments,
  onCommentPosted,
}: ClaimCommentsThreadProps) {
  const { t } = useTranslation();
  const { persona } = useDemoMode();
  const [body, setBody] = useState('');
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when comments change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments]);

  async function handlePost() {
    const trimmed = body.trim();
    if (!trimmed) return;
    setPosting(true);
    setPostError(null);
    try {
      await api.post<unknown>(`/approver/claims/${claimId}/comments`, { body: trimmed });
      setBody('');
      onCommentPosted();
    } catch (err) {
      setPostError(err instanceof Error ? err.message : t('errors.internalServerError'));
    } finally {
      setPosting(false);
    }
  }

  function getBubbleClass(comment: Comment): string {
    if (comment.isSystemMessage) {
      return 'bg-slate-100 text-slate-600 italic text-xs w-full rounded';
    }
    if (comment.authorType === 'BRAND') {
      return 'bg-copper-soft text-ink rounded ml-auto max-w-[80%]';
    }
    return 'bg-bone border border-hairline text-ink rounded mr-auto max-w-[80%]';
  }

  return (
    <div className="flex flex-col space-y-3">
      {/* Thread */}
      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
        {comments.length === 0 && (
          <p className="text-xs text-slate-400 text-center py-4">
            {t('approver.comments.empty', { defaultValue: 'No messages yet.' })}
          </p>
        )}

        {comments.map((c) => (
          <div
            key={c.id}
            className={['flex', c.isSystemMessage ? 'justify-center' : c.authorType === 'BRAND' ? 'justify-end' : 'justify-start'].join(' ')}
          >
            {c.isSystemMessage ? (
              <div className="flex items-start gap-2 px-3 py-2 bg-slate-100 rounded w-full">
                <span className="text-slate-400 text-xs shrink-0 mt-0.5">ℹ</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs italic text-slate-600">{c.body}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{formatDate(c.createdAt)}</p>
                </div>
              </div>
            ) : (
              <div className={getBubbleClass(c)}>
                <div className="px-3 py-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-slate-600">
                      {c.authorName ?? (c.authorType === 'BRAND' ? t('approver.comments.brand', { defaultValue: 'Brand' }) : t('approver.comments.distributor', { defaultValue: 'Distributor' }))}
                    </span>
                    <span className="text-xs text-slate-400">{formatDate(c.createdAt)}</span>
                  </div>
                  <p className="text-sm leading-relaxed">{c.body}</p>
                </div>
              </div>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Compose area */}
      <div className="border-t border-hairline pt-3 space-y-2">
        <p className="text-xs text-slate-500 font-medium">
          {t('approver.comments.postAs', { defaultValue: 'Posting as' })}{' '}
          <strong>{persona.displayName}</strong>
        </p>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t('approver.comments.placeholder', {
            defaultValue: 'Type a comment or request...',
          })}
          rows={3}
          className="w-full px-3 py-2 text-sm border border-hairline rounded bg-bone-soft focus:outline-none focus:border-copper resize-none placeholder:text-slate-400"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              void handlePost();
            }
          }}
        />
        {postError && (
          <p className="text-xs text-danger">{postError}</p>
        )}
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-400">
            {t('approver.comments.tip', { defaultValue: 'Ctrl+Enter to send' })}
          </p>
          <button
            type="button"
            disabled={posting || !body.trim()}
            onClick={() => { void handlePost(); }}
            className="px-4 py-1.5 bg-copper text-bone-soft rounded text-sm font-semibold hover:bg-copper-warm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {posting
              ? t('approver.comments.posting', { defaultValue: 'Posting...' })
              : t('approver.comments.post', { defaultValue: 'Post' })}
          </button>
        </div>
      </div>
    </div>
  );
}
